// `extension-alert-candidate-profile-action-confirmation-modal.tpl.html`, ported — the small box a
// profile action opens while it runs ("Preparing candidate details..."), then its result and an Ok.
//
// ⚠️ NOT THE UNIFIED TEMPLATE. `sendCandidateDetails` (`shortlist.candidate-actions.service.ng1.ts`)
// calls `modalService.show()` WITHOUT `useUnifiedTemplate`, so the chrome is
// `/common/templates/modal.dialog.tpl.html`: `.color-graydark > .modal-content.noborder >
// .modal-header(close, title, subtitle) + .modal-body`, and no footer because no button text is
// given. Rendering it through `UnifiedModal` would swap in `.modal-content-inner` and lose the
// header's title/subtitle rows, which are empty here but still take up their padding.
//
// ⚠️ AND THE WINDOW CLASS REPLACES THE DEFAULT. The caller passes `windowClass: "dialog-unified
// shortlist-dialogue extension-candidates-profile-action-modal"`; `angular.extend` overwrites
// `'dialog-base dialog-common'` whole, so there is no `dialog-base`, and with `useUnifiedTemplate`
// unset nothing rewrites it either.
//
// `.modal-content` is nested, as in `MessageDialog`: uib's own `.modal-content` wraps the
// template's `.modal-content.noborder`. ng-include's `<ng-include>` element around the body is not
// reproduced, as `UnifiedModal` does not reproduce it: an unstyled inline wrapper of block content.
import { useEffect, useRef, useState, type ReactElement } from 'react'
import { useModalLayer } from '../modal/modalStack.ts'

export const PROFILE_ACTION_MODAL_WINDOW_CLASS = 'dialog-unified shortlist-dialogue extension-candidates-profile-action-modal'

export interface ProfileActionConfirmationModalProps {
  /** `modalScope.inProgressMessage`, e.g. `CANDIDATE_DETAILS_IN_PROGRESS_MESSAGE`. */
  inProgressMessage: string
  /** `modalScope.okButtonText` — `PROFILE_ACTION_OK_BUTTON_TEXT` ("Ok") upstream. */
  okButtonText: string
  /**
   * The action. Called once, on open; resolves `ctrl.actionResultMessage`, which replaces the
   * spinner. `candidateDetailsResultMessage(() => sendCandidateDetails(id))` is the email action.
   */
  run: () => Promise<string>
  /** Ok, the header's ×, Escape and a backdrop click — upstream's result is read by no caller. */
  onClose: () => void
}

export function ProfileActionConfirmationModal({
  inProgressMessage,
  okButtonText,
  run,
  onClose,
}: ProfileActionConfirmationModalProps): ReactElement {
  const layer = useModalLayer(true)
  const windowRef = useRef<HTMLDivElement>(null)
  // `ctrl.isProfileActionInProgress` / `ctrl.actionResultMessage`.
  const [result, setResult] = useState<string | undefined>(undefined)
  const inProgress = result === undefined

  // ⚠️ ONCE. StrictMode runs effects twice; the ref keeps the action — which sends an email — to
  // a single request.
  const started = useRef(false)
  useEffect(() => {
    if (started.current) return
    started.current = true
    run().then(setResult, () => {
      // `run` is meant to resolve its own failure line; if it rejects anyway, end the spinner
      // rather than leave it turning.
      setResult('')
    })
  }, [run])

  // ui-bootstrap moves focus into the window on open.
  useEffect(() => {
    const win = windowRef.current
    if (win !== null && !win.contains(document.activeElement)) win.focus()
  }, [])

  // `keyboard: true` — Escape dismisses.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [onClose])

  return (
    <>
      {layer.backdropZIndex === undefined ? null : (
        <div className="modal-backdrop fade in" style={{ zIndex: layer.backdropZIndex }} />
      )}
      <div
        ref={windowRef}
        className={`modal fade ${PROFILE_ACTION_MODAL_WINDOW_CLASS} in`}
        role="dialog"
        tabIndex={-1}
        style={{ zIndex: layer.windowZIndex, display: 'block' }}
        onClick={(event) => {
          // `backdrop: true`: a click on the window element itself, never a child, dismisses.
          if (event.target === event.currentTarget) onClose()
        }}
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="color-graydark">
              <div className="modal-content noborder">
                <div className="modal-header">
                  <button type="button" className="close" onClick={onClose}>
                    <span aria-hidden="true">&times;</span>
                    <span className="sr-only">close</span>
                  </button>
                  <div className="modal-title" />
                  <div className="modal-subtitle" />
                </div>
                <div className="modal-body">
                  {/* `ng-show` upstream; rendered conditionally here, as `DeleteAlertPage` does —
                      `.ng-hide` is `display: none`, so the pixels are the same. */}
                  <div className="extension-alert-profile-action-overlay">
                    {inProgress ? (
                      <div>
                        <div className="extension-alert-profile-action-spinner">
                          <i className="fa fa-2x fa-spinner fa-spin" />
                        </div>
                        <div className="extension-candidates-profile-action-text candidates-text-center mT15">
                          {inProgressMessage}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="extension-candidates-profile-action-text profile-action-response">{result}</div>
                        <div className="extension-candidates-profile-action-button mT15">
                          {/* `click-and-disable="ok()"`; `ok()` closes at once, so there is no
                              in-flight window to disable across. */}
                          <button className="ok-button" onClick={onClose}>
                            {okButtonText}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
